/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { Control, Controller, UseFormRegisterReturn } from 'react-hook-form';
import {
  NumericFormat,
  NumericFormatProps,
  PatternFormat,
  PatternFormatProps,
} from 'react-number-format';

import cx from 'classnames';

import { FieldErrorMessage } from 'components/Form/FieldErrorMessage/FieldErrorMessage';

import s from './InputField.module.css';

export type TInputFieldProps = {
  error?: string;
  id: string;
  label: string;
  staticLabel?: boolean;
  name: string;
  register?: UseFormRegisterReturn;
  required?: boolean;
  type?: HTMLInputElement['type'];
  className?: string;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  placeholder?: HTMLInputElement['placeholder'];
  decimalScale?: number;
  control?: Control<any>;
};

const BaseInput = React.forwardRef<HTMLInputElement>((props, ref) => {
  return <input ref={ref} {...props} />;
});

BaseInput.displayName = 'BaseInput';

const PhoneInput = React.forwardRef<
  HTMLInputElement,
  Omit<PatternFormatProps, 'format' | 'mask'>
>((props, ref) => {
  return (
    <PatternFormat
      getInputRef={ref}
      className={s.input}
      format="+1 (###) ###-####"
      allowEmptyFormatting={false}
      mask="_"
      {...props}
    />
  );
});

PhoneInput.displayName = 'PhoneInput';

const PriceInput = React.forwardRef<HTMLInputElement, NumericFormatProps>(
  (props, ref) => {
    return (
      <NumericFormat
        getInputRef={ref}
        className={s.input}
        prefix="$"
        allowNegative={false}
        {...props}
      />
    );
  },
);

PriceInput.displayName = 'PriceInput';

const ZipCodeInput = React.forwardRef<
  HTMLInputElement,
  Omit<PatternFormatProps, 'format' | 'mask'>
>((props, ref) => {
  return (
    <PatternFormat
      getInputRef={ref}
      className={s.input}
      format="#####"
      allowEmptyFormatting={false}
      mask=""
      {...props}
    />
  );
});

ZipCodeInput.displayName = 'ZipCodeInput';

const InputField = React.forwardRef<HTMLInputElement, TInputFieldProps>(
  (
    {
      error,
      id,
      label,
      staticLabel = false,
      name,
      register,
      required,
      type = 'text',
      className,
      onKeyDown,
      placeholder,
      decimalScale,
      control,
    },
    ref,
  ) => {
    const Input = React.useMemo(() => {
      switch (type) {
        case 'tel':
          return PhoneInput;
        case 'zipcode':
          return ZipCodeInput;
        case 'textarea':
          return type;
        case 'price':
          return PriceInput;
        default:
          return BaseInput;
      }
    }, [type]);

    // NumericFormat doesn't support 'textarea' as a type, so this is to appease TS.
    const inputType = (
      type === 'textarea' ? 'text' : type
    ) as NumericFormatProps['type'];

    return (
      <div
        className={cx(s.root, error ? s.error : null, className, {
          [s.staticLabel]: staticLabel,
        })}
      >
        <label htmlFor={id} className={s.label}>
          <span className={s.labelText}>{label}</span>
          <div className={s.controlContainer}>
            {control ? (
              <Controller
                control={control}
                name={name}
                render={({ field: { onChange, onBlur, name, value } }) => (
                  <Input
                    // TODO - Sort out this TS stuff if possible
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ref={ref as any}
                    className={s.input}
                    type={inputType}
                    id={id}
                    aria-required={required}
                    aria-invalid={error ? 'true' : undefined}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder={placeholder ?? ' '}
                    decimalScale={decimalScale}
                  />
                )}
              />
            ) : (
              <Input
                className={s.input}
                // TODO - Sort out this TS stuff if possible
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ref={ref as any}
                type={inputType}
                id={id}
                aria-required={required}
                {...register}
                aria-invalid={error ? 'true' : undefined}
                onKeyDown={onKeyDown}
                placeholder={placeholder ?? ' '}
                decimalScale={decimalScale}
              />
            )}

            <FieldErrorMessage id={id} error={error} />
          </div>
        </label>
      </div>
    );
  },
);

InputField.displayName = 'InputField';

export { InputField };
